import { useCallback, useEffect, useRef, useState } from 'react'
import type { FocusEvent } from 'react'

const FOCUSABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

/**
 * A button that shows and hides a panel, with the behaviour `<details>` lacks.
 *
 * `<details>` stays open on an outside click, ignores Escape, and never returns
 * focus, so a menu built on it is only half a menu. This is the other half and
 * nothing more: it is a disclosure, not an ARIA menu, so it deliberately does
 * not implement arrow-key roving — Tab walks the panel, and leaving it closes
 * it.
 */
export function useDisclosure<T extends HTMLElement = HTMLButtonElement, P extends HTMLElement = HTMLDivElement>() {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<T>(null)
  const panelRef = useRef<P>(null)

  const toggle = useCallback(() => setOpen((current) => !current), [])
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    panelRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus()
    const inside = (target: EventTarget | null) =>
      target instanceof Node && Boolean(triggerRef.current?.contains(target) || panelRef.current?.contains(target))
    // Closing on an outside press does not move focus: the press already put
    // it wherever the person meant it to go.
    const onPointerDown = (event: Event) => { if (!inside(event.target)) setOpen(false) }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  /**
   * Tabbing out of the trigger-and-panel pair closes it. A focus loss with no
   * destination (the window blurring, a press on inert page) is left to the
   * pointer handler, so switching tabs does not dismiss the panel.
   */
  const onBlur = useCallback((event: FocusEvent) => {
    const next = event.relatedTarget
    if (!(next instanceof Node)) return
    if (triggerRef.current?.contains(next) || panelRef.current?.contains(next)) return
    setOpen(false)
  }, [])

  return { open, toggle, close, triggerRef, panelRef, onBlur }
}
